// PetInsuranceTopicsList.tsx
import React from "react";
import { Container, Row, Col, Card, ListGroup, Badge } from "react-bootstrap";

// If you have the interface in another file, import it:
// import type { PetInsuranceTopic } from "./PetInsuranceTopics";
export interface PetInsuranceTopic {
  id: string;
  title: string;
  titleImageUrl: string;
  items: string[]; // strings include **bold** markdown at the start
}

type Columns = 1 | 2 | 3;

export interface PetInsuranceTopicsListProps {
  topics: PetInsuranceTopic[];
  title?: string;
  /**
   * How many columns on desktop (md+). Defaults to 2.
   * 1 = full width, 2 = halves, 3 = thirds
   */
  columns?: Columns;
  /**
   * Image height for card headers (px). Defaults to 180.
   */
  imageHeightPx?: number;
}

/**
 * Parses a string like "**Bold lead:** rest of the sentence"
 * and returns a tuple [lead, rest]. If no markdown lead is found,
 * it returns [undefined, originalString].
 */
function splitBoldLead(s: string): [lead?: string, rest?: string] {
  const m = s.match(/^\s*\*\*(.+?)\*\*\s*:?\s*(.*)$/);
  if (m) {
    const lead = m[1]?.trim();
    const rest = (m[2] ?? "").trim();
    return [lead, rest || undefined];
  }
  return [undefined, s];
}

function colSizeFor(columns: Columns): number {
  // Bootstrap grid is 12 columns
  switch (columns) {
    case 1:
      return 12;
    case 2:
      return 6;
    case 3:
      return 4;
    default:
      return 6;
  }
}

export const PetInsuranceTopicsList: React.FC<PetInsuranceTopicsListProps> = ({
  topics,
  title,
  columns = 2,
  imageHeightPx = 180,
}) => {
  const mdSize = colSizeFor(columns);

  return (
    <Container className="py-4">
      {title && (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h2 className="m-0">{title}</h2>
          <Badge bg="secondary">{topics.length}</Badge>
        </div>
      )}

      <Row className="g-4">
        {topics.map((t) => (
          <Col key={t.id} xs={12} md={mdSize}>
            <Card className="h-100 shadow-sm">
              {t.titleImageUrl && (
                <Card.Img
                  variant="top"
                  src={t.titleImageUrl}
                  alt={t.title}
                  style={{ height: imageHeightPx, objectFit: "cover" }}
                />
              )}
              <Card.Body>
                <Card.Title className="mb-2">{t.title}</Card.Title>
              </Card.Body>

              <ListGroup variant="flush" className="pb-2">
                {t.items.map((it, idx) => {
                  const [lead, rest] = splitBoldLead(it);
                  return (
                    <ListGroup.Item key={`${t.id}-${idx}`}>
                      {lead ? (
                        <>
                          <strong>{lead}</strong>
                          {rest ? <>: {rest}</> : null}
                        </>
                      ) : (
                        it
                      )}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  );
};

export default PetInsuranceTopicsList;
